<template>
  <div :class="darkModeClass" class="tabs-root" :data-accent="panel.currentColor || 'primary'">
    <div class="tab-group">
      <slot>
        <Heading :level="1" v-text="panel.name" v-if="panel.showTitle" />

        <p
          v-if="panel.helpText"
          :class="panel.helpText ? 'mt-2' : 'mt-3'"
          class="text-gray-500 text-sm font-semibold italic"
          v-html="panel.helpText"
        ></p>
      </slot>

      <div :class="[panel.showTitle && !panel.showToolbar ? 'mt-3' : '']">
        <div class="tabs-shell">
          <div class="tabs-nav-wrap">
            <div class="tabs-nav-scroll">
              <nav aria-label="Tabs" class="tabs-nav nova-tabs">
                <a
                  v-for="(tab, key) in getSortedTabs(tabs)"
                  :key="key"
                  :dusk="tab.slug + '-tab'"
                  :class="{ 'is-active': getIsTabCurrent(tab) }"
                  class="tab-item"
                  :style="getTabWidth()"
                  @click.prevent="handleTabClick(tab)"
                >
                  <span class="tab-item__label">{{ tab.properties.title }}</span>
                  <span v-if="getIsTabCurrent(tab)" aria-hidden="true" class="tab-indicator"></span>
                </a>
              </nav>
            </div>
          </div>

          <div
            v-for="(tab, index) in getSortedTabs(tabs)"
            :key="'related-tabs-fields' + index"
            :ref="getTabRefName(tab)"
            :class="['tab', tab.slug]"
            class="tabs-panel-body"
            :label="tab.name"
            v-show="getIsTabCurrent(tab)"
          >
            <div>
              <div
                v-if="getAllFieldsExceptRelatable(tab).length"
                class="tabs-content rounded-b-2xl py-2 px-4 sm:px-6 divide-y divide-gray-100 dark:divide-gray-700"
              >
                <KeepAlive
                  v-for="(field, index) in getAllFieldsExceptRelatable(tab)"
                  :key="index"
                >
                  <component
                    :is="getComponentName(field)"
                    class="flex flex-col -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 md:py-0 space-y-2 md:space-y-0"
                    :class="{
                      'remove-bottom-border': index === tab.fields.length - 1,
                    }"
                    :field="field"
                    :index="index"
                    :resource="resource"
                    :resource-id="resourceId"
                    :resource-name="resourceName"
                    @actionExecuted="actionExecuted"
                  />
                </KeepAlive>
              </div>
              <div v-if="getAllRelatableFields(tab).length">
                <KeepAlive
                  v-for="(field, index) in getAllRelatableFields(tab)"
                  :key="index"
                >
                  <div class="mt-8">
                    <div
                      v-if="
                        getHeadingVisibilityForAttribute(tab, field.attribute)
                      "
                      class="border-b border-gray-400 dark:border-gray-700 pb-3 mb-5"
                    >
                      <h2
                        class="font-normal text-xl md:text-xl flex items-center"
                      >
                        <span>{{ field.name }}</span>
                      </h2>
                    </div>
                    <component
                      :is="getComponentName(field)"
                      :class="{
                        'remove-bottom-border': index === tab.fields.length - 1,
                      }"
                      :field="field"
                      :index="index"
                      :resource="resource"
                      :resource-id="resourceId"
                      :resource-name="resourceName"
                      @actionExecuted="actionExecuted"
                    />
                  </div>
                </KeepAlive>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BehavesAsPanel from '../mixins/BehavesAsPanel'
import HasTabs from '../mixins/HasTabs'

export default {
  mixins: [BehavesAsPanel, HasTabs],
  data: () => ({
    tabMode: 'detail',
  }),
  methods: {
    getAllFieldsExceptRelatable(tab) {
      return tab.fields.filter(field => !field.relatable)
    },
    getAllRelatableFields(tab) {
      return tab.fields.filter(field => field.relatable)
    },
    getTabWidth() {
      const tabsCount = this.getSortedTabs(this.tabs).length
      const minTabsForScroll = 5

      if (tabsCount < minTabsForScroll) {
        const widthPercent = 100 / tabsCount

        return {
          '--tab-width-desktop': `${widthPercent}%`,
          '--tab-width-mobile': `max(9.5rem, ${Math.min(widthPercent, 50)}%)`,
        }
      }

      return {
        '--tab-width-desktop': '20%',
        '--tab-width-mobile': '10.5rem',
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tabs-shell {
  overflow: hidden;
  border: 1px solid rgba(var(--colors-gray-200));
  border-radius: 1rem;
  background-color: rgb(var(--colors-white));
}

.dark .tabs-shell {
  border-color: rgba(var(--colors-gray-700));
  background-color: rgb(var(--colors-gray-800));
}

.tabs-nav-wrap {
  border-bottom: 1px solid rgba(var(--colors-gray-200));
  background-color: rgb(var(--colors-gray-50));
}

.dark .tabs-nav-wrap {
  border-bottom-color: rgba(var(--colors-gray-700));
  background-color: rgb(var(--colors-gray-900));
}

.tabs-nav-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.tabs-nav {
  display: flex;
  min-width: min-content;
}

.tab-item {
  position: relative;
  display: block;
  flex: 0 0 auto;
  min-width: var(--tab-width-mobile, 45%);
  min-height: 3.25rem;
  padding: 0.75rem 0.875rem;
  cursor: pointer;
  text-align: center;
  font-size: 0.8125rem;
  line-height: 1.35;
  font-weight: 500;
  color: rgba(var(--colors-gray-500));
  background-color: transparent;
  border-inline-end: 1px solid rgba(var(--colors-gray-200));
  transition: color 0.15s ease, background-color 0.15s ease;
}

.dark .tab-item {
  color: rgba(var(--colors-gray-400));
  border-inline-end-color: rgba(var(--colors-gray-700));
}

.tab-item:last-child {
  border-inline-end: none;
}

.tab-item:hover {
  color: rgba(var(--colors-gray-700));
  background-color: rgba(var(--colors-gray-100), 0.65);
}

.dark .tab-item:hover {
  color: rgba(var(--colors-gray-200));
  background-color: rgba(var(--colors-gray-800), 0.85);
}

.tab-item__label {
  display: block;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.tab-indicator {
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  height: 2px;
  background-color: transparent;
}

.tabs-root[data-accent='primary'] {
  --tab-accent: var(--colors-primary-500);
}

.tabs-root[data-accent='sky'] {
  --tab-accent: var(--colors-sky-500);
}

.tabs-root[data-accent='blue'] {
  --tab-accent: var(--colors-blue-500);
}

.tabs-root[data-accent='indigo'] {
  --tab-accent: var(--colors-indigo-500);
}

.tab-item.is-active {
  color: rgba(var(--tab-accent, var(--colors-primary-500)));
  font-weight: 700;
  background-color: rgb(var(--colors-white));
}

.dark .tab-item.is-active {
  background-color: rgb(var(--colors-gray-800));
}

.tab-item.is-active .tab-indicator {
  background-color: rgba(var(--tab-accent, var(--colors-primary-500)));
}

.tabs-content {
  background-color: rgb(var(--colors-white));
}

.dark .tabs-content {
  background-color: rgb(var(--colors-gray-800));
}

.tabs-panel-body {
  margin-top: 0;
}

@media (min-width: 640px) {
  .tab-item {
    min-width: var(--tab-width-desktop, 20%);
    min-height: 3.5rem;
    padding: 1rem;
    font-size: 0.875rem;
  }
}
</style>
